import { authService } from "services";
import moment from "moment";
import React from "react";
import i18next from "i18next";
import InlineAlert from "components/InlineAlert";
import {
	SUBSCRIPTION_TYPES,
	SUBSCRIPTION_FEATURES,
	PREMIUM_PLANS,
} from "constants/subscriptionTypes";
import { isHaddockHostname } from "utils/haddockDomain";

export function isLogin() {
	return authService.currentUser && authService.token;
}

export function isAdmin() {
	// return authService.currentUser && authService.currentUser.role === "ADMIN";
	return (
		authService.currentUser && authService.currentUser.roles.includes("ADMIN")
	);
}

export function isSupervisor() {
	return (
		authService.currentUser &&
		authService.currentUser.roles.includes("SUPERVISOR")
	);
}

export function getGroupsSupervised(user) {
	return user?.groups_supervised ?? authService.currentUser?.groups_supervised ?? [];
}

/** Supervisor con permiso de editar planificación (read_only = false) en al menos un grupo. */
export function isSupervisorWithEditPlanning(groups_supervised) {
	const groups = groups_supervised ?? getGroupsSupervised();
	return (
		isSupervisor() &&
		groups.length > 0 &&
		groups.some((gs) => !gs.read_only)
	);
}

/** Supervisor con permiso de crear/editar turnos en al menos un grupo. */
export function isSupervisorWithEditShifts(groups_supervised) {
	const groups = groups_supervised ?? getGroupsSupervised();
	return (
		isSupervisor() &&
		groups.length > 0 &&
		groups.some((gs) => gs.edit_shifts)
	);
}

/** Puede crear/editar ruedas de turno (admin o supervisor con permiso de planificación). */
export function canEditShiftPatterns(user) {
	if (isAdmin()) return true;
	return isSupervisorWithEditPlanning(getGroupsSupervised(user));
}

/**
 * Admin principal de la cuenta (heurística): empleado ADMIN creado dentro de las
 * primeras 12 horas tras company.created (no antes). Sirve para bloquear la edición
 * del email desde la ficha (cuenta de registro).
 *
 * No usamos "mismo día calendario" solo: bloqueaba a cualquier admin dado de alta
 * ese día aunque fueran muchas horas después del alta de la compañía (falsos positivos).
 */
export function isEmployeeCompanyFoundingAdmin(employee, company) {
	const employeeRoles = employee?.roles || [];
	const isAdminUser = Array.isArray(employeeRoles)
		? employeeRoles.includes("ADMIN")
		: false;
	if (!isAdminUser) return false;

	const employeeCreated = employee?.created || employee?.created_at;
	const companyCreated = company?.created || company?.created_at;
	if (!employeeCreated || !companyCreated) return false;
	const emp = moment(employeeCreated);
	const co = moment(companyCreated);
	if (!emp.isValid() || !co.isValid()) return false;
	if (emp.isBefore(co)) return false;
	const hoursAfterCompany = emp.diff(co, "hours", true);
	return hoursAfterCompany >= 0 && hoursAfterCompany <= 12;
}

/**
 * Determina si se debe incluir el parámetro include_supervised en las llamadas API
 * Solo se incluye si el usuario es supervisor y NO es admin
 * Los admins no necesitan este parámetro porque ya tienen acceso completo
 */
export function shouldIncludeSupervised() {
	return isSupervisor() && !isAdmin();
}

/**
 * ============================
 * FUNCIONES DE SUSCRIPCIÓN
 * ============================
 */

/**
 * Obtiene el tipo de suscripción actual del usuario
 * @returns {string|null} El nombre del tipo de suscripción o null si no existe
 */
export function getSubscriptionType() {
	const user = authService.currentUser;
	return user?.subscription?.subscription_type?.name || null;
}

/**
 * Obtiene el objeto de suscripción completo del usuario
 * @returns {object|null} El objeto de suscripción o null
 */
export function getSubscription() {
	const user = authService.currentUser;
	return user?.subscription || null;
}

/**
 * Verifica si el usuario tiene una suscripción Pro
 * @returns {boolean}
 */
export function isPro() {
	return getSubscriptionType() === SUBSCRIPTION_TYPES.PRO;
}

/**
 * Verifica si el usuario tiene una suscripción Friends
 * Friends tiene los mismos permisos que Pro
 * @returns {boolean}
 */
export function isFriends() {
	return getSubscriptionType() === SUBSCRIPTION_TYPES.FRIENDS;
}

/**
 * Verifica si el usuario tiene una suscripción Trial
 * @returns {boolean}
 */
export function isTrial() {
	return getSubscriptionType() === SUBSCRIPTION_TYPES.TRIAL;
}

/**
 * Verifica si el usuario tiene una suscripción Free
 * @returns {boolean}
 */
export function isFree() {
	return getSubscriptionType() === SUBSCRIPTION_TYPES.FREE;
}

/**
 * Verifica si el usuario tiene una suscripción Básico Plain
 * @returns {boolean}
 */
export function isBasicPlain() {
	return getSubscriptionType() === SUBSCRIPTION_TYPES.BASIC_PLAIN;
}

/**
 * Verifica si el usuario tiene una suscripción Básico Haddock
 * @returns {boolean}
 */
export function isBasicHaddock() {
	return getSubscriptionType() === SUBSCRIPTION_TYPES.BASIC_HADDOCK;
}

/**
 * Verifica si el usuario tiene cualquier tipo de suscripción básica
 * @returns {boolean}
 */
export function isBasic() {
	return isBasicPlain() || isBasicHaddock();
}

/**
 * Verifica si el usuario tiene un plan premium (Pro o Friends)
 * Friends tiene los mismos permisos que Pro
 * @returns {boolean}
 */
export function isPremium() {
	const currentSubscription = getSubscriptionType();
	return PREMIUM_PLANS.includes(currentSubscription);
}

/**
 * Verifica si el usuario tiene acceso a una funcionalidad específica
 * basado en su tipo de suscripción
 * @param {string} featureKey - La clave de la funcionalidad de SUBSCRIPTION_FEATURES
 * @returns {boolean} true si tiene acceso, false si no
 */
export function hasSubscriptionFeature(featureKey) {
	const feature = SUBSCRIPTION_FEATURES[featureKey];
	if (!feature) {
		console.warn(
			`Feature ${featureKey} no encontrada en SUBSCRIPTION_FEATURES`
		);
		return false;
	}

	const currentSubscription = getSubscriptionType();
	if (!currentSubscription) {
		return false;
	}

	return feature.allowedPlans.includes(currentSubscription);
}

/**
 * Obtiene el mensaje de upgrade para una funcionalidad
 * @param {string} featureKey - La clave de la funcionalidad de SUBSCRIPTION_FEATURES
 * @returns {string|null} El mensaje de upgrade o null
 */
export function getFeatureUpgradeMessage(featureKey) {
	const feature = SUBSCRIPTION_FEATURES[featureKey];
	return feature?.upgradeMessage || null;
}

export function getTrialDays() {
	const user = authService.currentUser;
	const now = new Date();
	const startTime = moment.utc(user.subscription.start_day).local();
	const totalDays = startTime.diff(moment(now), "days") + 15;
	return totalDays;
}

export function handleResponse(response, options = {}) {
	// console.log(response);
	if (response.redirected) {
		// Solo redirigir si la URL es del mismo origen o si estamos en producción
		const currentOrigin = window.location.origin;
		const redirectUrl = new URL(response.url);

		// Si la redirección es al mismo origen, permitirla directamente
		if (redirectUrl.origin === currentOrigin) {
			window.location.href = response.url;
			return {};
		}

		// En producción (no localhost), permitir redirecciones externas
		if (
			!currentOrigin.includes("localhost") &&
			!currentOrigin.includes("127.0.0.1")
		) {
			window.location.href = response.url;
			return {};
		}

		// En desarrollo: bloquear redirecciones externas y solo mostrar advertencia
		// No aplicar la redirección local, ya que puede ser incorrecta
		console.warn("Redirección externa bloqueada en desarrollo:", response.url);
		console.warn("En producción, esto redigiría a:", response.url);

		// No hacer nada - dejar que el flujo normal continúe sin redirección
		return {};
	}

	return response.text().then((text) => {
		// console.log(response);
		// console.log(text);
		let data;
		try {
			data = text ? JSON.parse(text) : {};
		} catch (error) {
			console.error("Error parsing response JSON:", error);
			data = {
				errors: [{ description: "Error procesando la respuesta del servidor" }],
			};
		}

		if (!response.ok) {
			// Manejo específico de errores basado en el código de estado
			let errors = [];
			switch (response.status) {
				case 401:
					if (!options.ignore401) {
						console.log(response);

						console.log("401 no ignorado, cerrando sesión");
						authService.logout();
						// window.location.reload(true);
					} else {
						console.log("401 ignorado en", response.url);
					}
					break;
				case 403:
					errors.push({ description: "Error de permisos" });
					break;
				case 500:
					errors.push({ description: "Ha habido un error de servidor" });
					break;
				// Añadir más casos según sea necesario
			}
			if (errors.length > 0) {
				data = { errors };
			}
		}

		data.ok = response.ok;
		data.status = response.status;
		// console.log(data);
		return data;
	});
}

export function handleImgResponse(response) {
	// console.log(response);
	return response.text().then((text) => {
		if (response.status === 404) {
			return "";
		}

		// console.log(data);
		return text;
	});
}

export function handleBlobResponse(response) {
	return response.blob().then((blob) => {
		if (response.status === 404) {
			return "";
		}

		return blob;
	});
}

// error formating in forms
// export function errorFormating(response) {
// 	//console.log(response);
// 	var errors = {};
// 	if (
// 		response.errors !== undefined &&
// 		Object.keys(response.errors).length > 0
// 	) {
// 		// console.log(response.errors)
//
// 		if (
// 			Object.keys(response.errors).length === 1 &&
// 			response.errors[0].code === undefined
// 		) {
// 			errors.general = response.errors[0].description;
// 		} else {
// 			response.errors.forEach((error) => {
// 				if (error.fields.length > 0) {
// 					error.fields.forEach((field) => {
// 						errors[field] = error.description;
//
// 						if (error.value) {
// 							errors[field + "_value"] = error.value;
// 						}
// 					});
// 				} else {
// 					errors[error.code] = error.description;
// 				}
// 			});
// 		}
// 	}
// 	//console.log(errors);
// 	return errors;
// }
// error formatting in forms
// export function errorFormating(response) {
// 	const errors = {};
//
// 	const errorList = Array.isArray(response?.errors)
// 		? response.errors
// 		: (response?.code || response?.description || response?.fields)
// 			? [response]
// 			: [];
//
// 	for (const err of errorList) {
// 		const desc = err?.description || err?.message || "Se ha producido un error.";
//
// 		if (Array.isArray(err?.fields) && err.fields.length > 0) {
// 			for (const field of err.fields) {
// 				errors[field] = desc;
// 				if (err.value !== undefined) {
// 					errors[`${field}_value`] = err.value;
// 				}
// 			}
// 		} else if (err?.code) {
// 			errors[err.code] = desc;
// 		}
//
// 		// Mensaje general (primero disponible)
// 		if (!errors.general) errors.general = desc;
// 	}
//
// 	// Para respuestas sin errors[]
// 	if (!errorList.length && response?.description) {
// 		errors.general = response.description;
// 		if (Array.isArray(response.fields)) {
// 			for (const f of response.fields) errors[f] = response.description;
// 		}
// 	}
//
// 	return errors;
// }
export function errorFormating(
	response,
	{ includeFieldsInGeneral = true, joiner = ", " } = {}
) {
	const errors = {};

	const errorList = Array.isArray(response?.errors)
		? response.errors
		: response?.code || response?.description || response?.fields
			? [response]
			: [];

	const generalSet = new Set();

	for (const err of errorList) {
		const desc =
			err?.description || err?.message || "Se ha producido un error.";

		if (Array.isArray(err?.fields) && err.fields.length > 0) {
			for (const field of err.fields) {
				errors[field] = desc;
				if (err.value !== undefined) {
					errors[`${field}_value`] = err.value;
				}
				if (includeFieldsInGeneral) generalSet.add(desc);
			}
		} else {
			if (err?.code) errors[err.code] = desc;
			generalSet.add(desc);
		}
	}

	// Caso sin errors[] pero con description/fields
	if (!errorList.length && response?.description) {
		if (Array.isArray(response.fields) && response.fields.length > 0) {
			for (const f of response.fields) {
				errors[f] = response.description;
				if (includeFieldsInGeneral) generalSet.add(response.description);
			}
		} else {
			generalSet.add(response.description);
		}
	}

	// Asigna general sin duplicados (si hay algo que mostrar)
	if (generalSet.size > 0) {
		errors.general = Array.from(generalSet).join(joiner);
	}

	return errors;
}

/**
 * Maps employee create/update API errors to form fields.
 * Duplicate email in the same company must be HTTP 400 with
 * { errors: [{ key: "employee.email_exists", code: "exists", fields: ["email"] }] }.
 * A 500 is not a duplicate-email signal; it is shown as a generic server error.
 */
export function formatEmployeeSaveErrors(response) {
	const formatted = errorFormating(response);
	const list = Array.isArray(response?.errors) ? response.errors : [];
	const emailExists = list.some(
		(err) =>
			err?.key === "employee.email_exists" ||
			(err?.code === "exists" &&
				Array.isArray(err?.fields) &&
				err.fields.includes("email"))
	);

	if (emailExists) {
		formatted.email = i18next.t("employee.emailExists");
		delete formatted.general;
	}

	return formatted;
}

export function renderErrorList(response) {
	if (!response?.errors?.length) {
		return null;
	}

	return (
		<InlineAlert
			variant="error"
			className="mt15"
			message={i18next.t("common.errorsFound")}
			details={
				<ul className="inline-alert__list">
					{response.errors.map((error, index) => (
						<li key={index}>
							{error.description} {error.value && `(${error.value})`}
						</li>
					))}
				</ul>
			}
			detailsOpen
		/>
	);
}

// geolocation + IP to add to requests

export function getGeoIP() {
	var GeoIPObj = { lat: 0, lon: 0, ip: "" }; // Removed default "1.2.3.4"

	// if geolocation is enabled lat & lon coords are more accurated
	if ("geolocation" in navigator) {
		navigator.geolocation.getCurrentPosition(function (position) {
			// console.log(position.coords.latitude, position.coords.longitude);
			GeoIPObj.lat = position.coords.latitude;
			GeoIPObj.lon = position.coords.longitude;
			// console.log(GeoIPObj);
			setGeoIP(GeoIPObj);
		});
	}

	return new Promise((resolve) => {
		console.log("DEBUG: Starting getGeoIP fetch");
		// Simple GET request to avoid strict CORS preflight issues with credentials
		fetch("https://api.ipify.org/?format=json")
			.then(function (response) {
				return response.json();
			})
			.then(function (result) {
				console.log("DEBUG: IP fetched successfully:", result.ip);
				// GeoIPObj = result;
				GeoIPObj.ip = result.ip;
				setGeoIP(GeoIPObj);
				resolve();
			})
			.catch(function (error) {
				console.error("DEBUG: IP fetch error:", error);
				// Resolve anyway so the app can start
				setGeoIP(GeoIPObj);
				resolve();
			});
	});
}

// expected output: X-Geo-IP: city:Rome, country:IT, lat:44.2134, lon:12.241, ip:1.2.3.4
export function setGeoIP(GeoIPObj) {
	// global.GetIPHeader = `city:${GeoIPObj.city}, country:${GeoIPObj.countryCode}, lat:${GeoIPObj.lat}, lon:${GeoIPObj.lon}, ip:${GeoIPObj.query}`;
	global.GetIPHeader = `lat:${GeoIPObj.lat}, lon:${GeoIPObj.lon}, ip:${GeoIPObj.ip}`;
	// console.log(global.GetIPHeader);
}

export function chunkArray(array, size) {
	const result = [];
	for (let i = 0; i < array.length; i += size) {
		result.push(array.slice(i, i + size));
	}
	return result;
}

const parseColorToRgb = (color) => {
	if (!color || typeof color !== "string") return null;
	const normalized = color.trim().toLowerCase();

	if (normalized === "black") return { r: 0, g: 0, b: 0 };
	if (normalized === "white") return { r: 255, g: 255, b: 255 };

	if (normalized.startsWith("rgb")) {
		const parts = normalized.match(/\d+(\.\d+)?/g);
		if (!parts || parts.length < 3) return null;
		return {
			r: Number(parts[0]),
			g: Number(parts[1]),
			b: Number(parts[2]),
		};
	}

	let hex = normalized.replace("#", "");
	if (hex.length === 3) {
		hex = hex
			.split("")
			.map((ch) => ch + ch)
			.join("");
	}
	if (hex.length !== 6) return null;

	const r = parseInt(hex.slice(0, 2), 16);
	const g = parseInt(hex.slice(2, 4), 16);
	const b = parseInt(hex.slice(4, 6), 16);
	if ([r, g, b].some((n) => Number.isNaN(n))) return null;

	return { r, g, b };
};

const relativeLuminance = ({ r, g, b }) => {
	const channel = (v) => {
		const c = v / 255;
		return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
	};
	return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
};

/**
 * Oscurece un color mezclándolo con negro (equivalente visual a
 * overlay rgba(0,0,0, amount) sobre fondo opaco).
 * amount: 0–1. Devuelve #RRGGBB o el color original si no se puede parsear.
 */
export function darkenColor(color, amount = 0.14) {
	if (!color || typeof color !== "string") return color;
	const clamped = Math.min(Math.max(Number(amount) || 0, 0), 1);
	if (clamped === 0) return color;

	const rgb = parseColorToRgb(color);
	if (!rgb) return color;

	const mix = (channel) => Math.round(channel * (1 - clamped));
	const toHex = (n) => n.toString(16).padStart(2, "0");
	return `#${toHex(mix(rgb.r))}${toHex(mix(rgb.g))}${toHex(mix(rgb.b))}`;
}

/** Fondos que se confunden con la rejilla blanca (sin color, transparentes o muy claros). */
export function isLightColor(color, threshold = 0.88) {
	if (color == null || typeof color !== "string") return true;
	const normalized = color.trim().toLowerCase();
	if (!normalized || normalized === "transparent" || normalized === "none") {
		return true;
	}
	if (normalized.startsWith("rgba")) {
		const parts = normalized.match(/\d+(\.\d+)?/g);
		if (parts && parts.length >= 4 && Number(parts[3]) === 0) {
			return true;
		}
	}
	const rgb = parseColorToRgb(color);
	if (!rgb) return true;
	return relativeLuminance(rgb) >= threshold;
}

/** Fondo efectivo para pintar: si falta o es transparente → blanco. */
export function resolveShiftBackgroundColor(color) {
	if (color == null || typeof color !== "string") return "#FFFFFF";
	const normalized = color.trim().toLowerCase();
	if (!normalized || normalized === "transparent" || normalized === "none") {
		return "#FFFFFF";
	}
	if (normalized.startsWith("rgba")) {
		const parts = normalized.match(/\d+(\.\d+)?/g);
		if (parts && parts.length >= 4 && Number(parts[3]) === 0) {
			return "#FFFFFF";
		}
	}
	return color;
}

// color de contraste para textos
export function getContrastColor(hexcolor, defaultColor = "black") {
	const darkColor = defaultColor;
	if (!hexcolor) return darkColor;

	const contrastRatio = (lumA, lumB) => {
		const lighter = Math.max(lumA, lumB);
		const darker = Math.min(lumA, lumB);
		return (lighter + 0.05) / (darker + 0.05);
	};

	const backgroundRgb = parseColorToRgb(hexcolor);
	if (!backgroundRgb) return darkColor;

	const darkRgb = parseColorToRgb(darkColor) || { r: 0, g: 0, b: 0 };
	const whiteRgb = { r: 255, g: 255, b: 255 };

	const bgLum = relativeLuminance(backgroundRgb);
	const darkLum = relativeLuminance(darkRgb);
	const whiteLum = relativeLuminance(whiteRgb);

	const contrastWithDark = contrastRatio(bgLum, darkLum);
	const contrastWithWhite = contrastRatio(bgLum, whiteLum);

	return contrastWithWhite >= contrastWithDark ? "white" : darkColor;
}
export const getAppName = () => {
	const domain = window.location.hostname;

	if (isHaddockHostname(domain)) {
		return "haddock HR";
	}
	return "Plain";
};

export const getHelpUrl = () => {
	const domain = window.location.hostname;

	if (isHaddockHostname(domain)) {
		return "https://support.haddock.app/es/category/haddock-hr-1qkk7xp/";
	}
	return "https://help.plain.ninja/es/";
};

export const calcAssigmentsStats = (assigmentsData) => {
	const targetHours = assigmentsData.reduce(
		(acc, assigment) => acc + assigment.target_hours,
		0
	);
	const currentHours = assigmentsData.reduce(
		(acc, assigment) => acc + assigment.current_hours,
		0
	);
	const neededHours = assigmentsData.reduce(
		(acc, assigment) => acc + assigment.needed_hours,
		0
	);
	return { targetHours, currentHours, neededHours };
};

// Format number with Spanish format (dot for thousands, comma for decimals)
export const formatNumber = (value) => {
	// Mantener placeholders o valores vacios sin romper
	if (value === null || value === undefined || value === "") return "0";
	if (typeof value === "string" && value.trim() === "—") return value;

	// Aceptar strings numericos con coma o punto decimal
	const normalizedValue =
		typeof value === "string" ? normalizeDecimal(value).trim() : value;
	const numericValue = Number(normalizedValue);

	// Si no es numerico, devolver el valor original para evitar errores de render
	if (!Number.isFinite(numericValue)) return value;

	if (Number.isInteger(numericValue) || numericValue === 0) {
		return Math.round(numericValue)
			.toString()
			.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
	}

	return numericValue
		.toFixed(2)
		.replace(".", ",")
		.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
};

// Patrón para entrada de decimales en formato español (coma decimal, punto miles)
export const DECIMAL_INPUT_PATTERN = /^[0-9]{0,3}(\.[0-9]{3})*(,[0-9]*)?$|^[0-9]*([,\.][0-9]*)?$/;

export const isValidDecimalInput = (value) => {
	if (value === "" || value === null || value === undefined) return true;
	return DECIMAL_INPUT_PATTERN.test(String(value).trim());
};

export const normalizeDecimal = (value) => {
	if (value === null || value === undefined || value === "") return value;
	const str = value.toString().trim();
	if (!str) return value;

	// Formato español: 1.234,56 -> 1234.56
	if (str.includes(",")) {
		return str.replace(/\./g, "").replace(",", ".");
	}

	const dotCount = (str.match(/\./g) || []).length;
	if (dotCount > 1) return str.replace(/\./g, "");

	// Un solo punto: miles (1.000) o decimal anglosajón (0.3)
	if (dotCount === 1) {
		const [, fraction = ""] = str.split(".");
		if (fraction.length === 3 && /^\d{3}$/.test(fraction)) {
			return str.replace(".", "");
		}
	}

	return str;
};

// Valor para inputs: vacío solo si el 0 viene del servidor (número).
// Si el usuario escribe "0" o "0,3", se muestra tal cual.
export const formatDecimalFieldValue = (value) => {
	if (value === null || value === undefined || value === "") return "";
	if (value === 0) return "";
	if (typeof value === "string") return value;
	return formatDecimalForInput(value);
};

// Elimina ceros a la izquierda al escribir (p. ej. "09" -> "9"), sin afectar "0,3"
export const sanitizeDecimalInput = (value) => {
	if (!value || typeof value !== "string") return value;
	if (value.includes(",") || value.includes(".")) return value;
	if (/^0\d+$/.test(value)) return value.replace(/^0+/, "") || "0";
	return value;
};

// Formatea un número para mostrarlo en un input de texto (coma decimal, punto miles)
export const formatDecimalForInput = (value) => {
	if (value === null || value === undefined || value === "") return "";
	if (typeof value === "string") {
		const trimmed = value.trim();
		if (trimmed === "" || trimmed === "—") return "";
		if (!Number.isFinite(Number(normalizeDecimal(trimmed)))) return trimmed;
		value = Number(normalizeDecimal(trimmed));
	}

	const numericValue = Number(value);
	if (!Number.isFinite(numericValue)) return String(value);

	const fixed = numericValue.toFixed(10).replace(/\.?0+$/, "");
	const [intPart, decPart] = fixed.split(".");
	const formattedInt = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
	return decPart !== undefined ? `${formattedInt},${decPart}` : formattedInt;
};

export const countries = [
	{ name: "Afganistán", iso: "AF" },
	{ name: "Albania", iso: "AL" },
	{ name: "Argelia", iso: "DZ" },
	{ name: "Angola", iso: "AO" },
	{ name: "Argentina", iso: "AR" },
	{ name: "Australia", iso: "AU" },
	{ name: "Austria", iso: "AT" },
	{ name: "Bangladés", iso: "BD" },
	{ name: "Bielorrusia", iso: "BY" },
	{ name: "Bélgica", iso: "BE" },
	{ name: "Brasil", iso: "BR" },
	{ name: "Canadá", iso: "CA" },
	{ name: "China", iso: "CN" },
	{ name: "Colombia", iso: "CO" },
	{ name: "Dinamarca", iso: "DK" },
	{ name: "Egipto", iso: "EG" },
	{ name: "Francia", iso: "FR" },
	{ name: "Alemania", iso: "DE" },
	{ name: "Grecia", iso: "GR" },
	{ name: "India", iso: "IN" },
	{ name: "Indonesia", iso: "ID" },
	{ name: "Irlanda", iso: "IE" },
	{ name: "Israel", iso: "IL" },
	{ name: "Italia", iso: "IT" },
	{ name: "Japón", iso: "JP" },
	{ name: "Kenia", iso: "KE" },
	{ name: "México", iso: "MX" },
	{ name: "Países Bajos", iso: "NL" },
	{ name: "Nueva Zelanda", iso: "NZ" },
	{ name: "Nigeria", iso: "NG" },
	{ name: "Noruega", iso: "NO" },
	{ name: "Pakistán", iso: "PK" },
	{ name: "Perú", iso: "PE" },
	{ name: "Filipinas", iso: "PH" },
	{ name: "Polonia", iso: "PL" },
	{ name: "Portugal", iso: "PT" },
	{ name: "Rusia", iso: "RU" },
	{ name: "Arabia Saudita", iso: "SA" },
	{ name: "Sudáfrica", iso: "ZA" },
	{ name: "Corea del Sur", iso: "KR" },
	{ name: "España", iso: "ES" },
	{ name: "Suecia", iso: "SE" },
	{ name: "Suiza", iso: "CH" },
	{ name: "Tailandia", iso: "TH" },
	{ name: "Turquía", iso: "TR" },
	{ name: "Emiratos Árabes Unidos", iso: "AE" },
	{ name: "Reino Unido", iso: "GB" },
	{ name: "Estados Unidos", iso: "US" },
	{ name: "Venezuela", iso: "VE" },
	{ name: "Vietnam", iso: "VN" },
];
